export const sha256Hash = async (input: string) => {
  const crypto = window.crypto;
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hash));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

// Anya Response Status Utilities
export interface Visualization {
  title?: string;
  type: "chart" | "trend" | "table" | "bar" | "code";
  data?: Array<{
    dataKey: string;
    [key: string]: number | string | null;
  }>;
  color?: {
    [key: string]: string;
  };
}

export interface AnyaResponse {
  reasoningSummary?: string;
  details?: string;
  visualisation?: Visualization[];
  nextQuestions?: string[];
  requestId?: string | null;
  conversationId?: string | null;
  status?: string;
  eventData?: Record<string, unknown> | null;
  displayStatus?: string;
}

const STATUS = {
  IN_PROGRESS: "In Progress",
  CANCELLED: "CANCELLED",
  AWAITING_CONFIRMATION: "AWAITING_CONFIRMATION",
} as const;

const checkStatus = (
  response: AnyaResponse | null | undefined,
  status: string,
  caseSensitive = false,
): boolean => {
  if (!response?.status) return false;
  return caseSensitive
    ? response.status === status
    : response.status.toLowerCase() === status.toLowerCase();
};

export const isStatusInProgress = (
  response: AnyaResponse | null | undefined,
): boolean => checkStatus(response, STATUS.IN_PROGRESS);

export const isStatusNotInProgress = (
  response: AnyaResponse | null | undefined,
): boolean => !checkStatus(response, STATUS.IN_PROGRESS);

export const isStatusCancelled = (
  response: AnyaResponse | null | undefined,
): boolean => checkStatus(response, STATUS.CANCELLED);

export const isStatusAwaitingConfirmation = (
  response: AnyaResponse | null | undefined,
): boolean => checkStatus(response, STATUS.AWAITING_CONFIRMATION);

export const isStatusNotAwaitingConfirmation = (
  response: AnyaResponse | null | undefined,
): boolean => !checkStatus(response, STATUS.AWAITING_CONFIRMATION);

export const isResponseCompleted = (
  response: AnyaResponse | null | undefined,
): boolean =>
  isStatusNotInProgress(response) && isStatusNotAwaitingConfirmation(response);

export const isResponseReadyToDisplay = (
  response: AnyaResponse | null | undefined,
): boolean => isStatusCancelled(response) || isResponseCompleted(response);

export const shouldShowDetails = (
  response: AnyaResponse | null | undefined,
): boolean =>
  Boolean(
    response?.details && response?.status && isResponseCompleted(response),
  );

export const shouldShowReasoningSummary = (
  response: AnyaResponse | null | undefined,
  isCancelled: boolean,
): boolean =>
  Boolean(
    response?.reasoningSummary &&
      response.reasoningSummary !== "" &&
      (isCancelled || isResponseReadyToDisplay(response)),
  );

export const hasPollingTimedOut = (
  startTime: number | null,
  maxPollTime: number,
): boolean => {
  if (!startTime) return false;
  const elapsedTime = Date.now() - startTime;
  return elapsedTime >= maxPollTime;
};
